import {
  createRootRoute,
  Outlet,
  Link,
  redirect,
  useRouter,
  useNavigate,
} from "@tanstack/react-router";
import { AppLayout } from "@charphq/ui/components/layout";
import { NotFound } from "@charphq/ui/components/layout/pages/NotFound";
import { useAuthContext } from "@charphq/ui/providers/auth-provider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WEB_NAV_ITEMS } from "../lib/navigation";
import { navOrganizationsQuery } from "../lib/organizations";
import { authClient } from "../lib/auth-client";
import { getAuthVerdict, invalidateAuthCache } from "../lib/auth-guards";
import { useCallback, useEffect, useMemo } from "react";

type GlobalWithProcessEnv = typeof globalThis & {
  process?: { env?: Record<string, string | undefined> };
};

export const Route = createRootRoute({
  // Single network-based auth gate for the whole app. Runs before any route
  // loads, so the previously-unguarded data routes (customers, devices, …) can
  // no longer render while logged out and 401-storm. /auth is the only public
  // surface. Keeping one source of truth (the session network check) here also
  // removes the cache-vs-network disagreement that caused the /auth ↔ /dashboard
  // redirect ping-pong.
  beforeLoad: async ({ location }) => {
    if (location.pathname === "/auth" || location.pathname.startsWith("/auth/")) {
      return;
    }
    // Fail open: only a definitive `unauthenticated` redirects. On `unknown`
    // (network/429/5xx) we let the route load and let its own queries surface
    // the error, so a transient/rate-limited get-session can't bounce an
    // authenticated user into an /auth ↔ /dashboard loop. The verdict is
    // cached, so this does not fire a network call on every navigation.
    if ((await getAuthVerdict()) === "unauthenticated") {
      throw redirect({
        to: "/auth",
        search: { redirect: location.pathname },
      });
    }
  },
  notFoundComponent: function NotFoundComponent() {
    return (
      <NotFound
        message="Page not found"
        description="The page you're looking for doesn't exist or has been moved."
        showBackButton={true}
        showHelpOptions={true}
      />
    );
  },
  errorComponent: function ErrorComponent({ error }) {
    // Handle redirect errors by re-throwing them
    if (error instanceof Response && error.status === 307) {
      throw error;
    }

    // Handle other errors
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-status-alert mb-4">
            Something went wrong
          </h1>
          <p className="text-muted-foreground mb-4">
            {error instanceof Error ? error.message : "An unexpected error occurred"}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  },
  component: function RootComponent() {
    const { isAuthenticated, user, session, signOut } = useAuthContext();
    // Deliberately no useRouterState() here: a bare (unselected) subscription
    // re-rendered the root on every router mutation — navigations, pending
    // transitions and hover preloads — and the root must re-render as rarely as
    // possible because it renders AppLayout above the whole page (OTO-158).
    // The dev route log below subscribes imperatively instead.
    const router = useRouter();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const envProcess = (globalThis as GlobalWithProcessEnv).process;
    const env = (envProcess?.env ?? {}) as Record<string, string | undefined>;
    const isDev = env.NODE_ENV === "development";

    const userInfo = useMemo(
      () => (user ? { name: user.name, email: user.email } : undefined),
      [user],
    );

    // Organizations the user belongs to, for the active-org switcher. Gated on auth so
    // the login screen never fires an org request. Uses the membership-scoped list
    // (super admins see all orgs and can act within any of them).
    // notifyOnChangeProps: only re-render the root when the org list itself
    // changes — not on every isFetching toggle (each background refetch used to
    // re-render the root twice, which sat above the whole page; OTO-158).
    const orgsQuery = useQuery({
      ...navOrganizationsQuery,
      enabled: isAuthenticated,
      notifyOnChangeProps: ["data"],
    });
    const organizations = orgsQuery.data ?? [];
    const activeOrganizationId = (session as { activeOrganizationId?: string } | null)
      ?.activeOrganizationId;

    const handleSwitchOrganization = useCallback(
      async (organizationId: string) => {
        await authClient.organization.setActive({ organizationId });
        // The active org changed → drop the cached auth verdict and reload all
        // org-scoped data so the whole app reflects the new tenant.
        invalidateAuthCache();
        await queryClient.invalidateQueries();
      },
      [queryClient],
    );

    const handleSignIn = useCallback(() => {
      navigate({ to: "/auth" });
    }, [navigate]);

    // Sign out, then navigate to /auth deterministically. Without the explicit
    // navigation the current (now-unauthenticated) page lingers until something
    // 401s, which is what triggered the redirect storm.
    const handleSignOut = useCallback(async () => {
      await signOut();
      // Drop the cached session so the next guard sees the signed-out state
      // immediately instead of a stale "authenticated" verdict.
      invalidateAuthCache();
      navigate({ to: "/auth", replace: true });
    }, [signOut, navigate]);

    useEffect(() => {
      if (!isDev) return;
      return router.subscribe("onResolved", ({ toLocation }) => {
        // eslint-disable-next-line no-console
        console.log("[CURRENT ROUTE CHANGED]", {
          pathname: toLocation.pathname,
          search: toLocation.search,
          hash: toLocation.hash,
          fullPath: toLocation.href,
        });
      });
    }, [isDev, router]);

    return (
      <AppLayout
        navItems={WEB_NAV_ITEMS}
        isAuthenticated={isAuthenticated}
        user={userInfo}
        userRole={user?.role}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        Link={Link}
        organizations={organizations}
        activeOrganizationId={activeOrganizationId}
        onSwitchOrganization={handleSwitchOrganization}
      >
        <Outlet />
      </AppLayout>
    );
  },
});
